import { FunctionComponent, useState, useRef, useEffect } from "react"
import {
  useGetOrganizationResultsAccessStatusQuery,
  useGetAdminOrganizationsQuery,
} from "../../app/services/meseeknaApiV2"
import { RootState, useTypedSelector } from "../../app/store"
import { useOrganizationRecovery } from "../../hooks/useOrganizationRecovery"
import {
  useProcessQueryMutation,
  useFindPeopleMatchesMutation,
  PersonMatch,
  QueryResult,
} from "../../app/services/chatbotApi"
import { RoleName } from "../../features/auth/types"
import OrganizationSelector from "../../components/organization-selector"
import Sidebar from "../../features/sidebar/components/sidebar"
import HeaderSection from "../../components/header-section"

type WhoThinksSectionProps = {
  showOrgSelector?: boolean
  standalone?: boolean // When true, includes Sidebar and HeaderSection
}

type ConversationMessage = {
  id: string
  type: "user" | "assistant"
  content: string
  matches?: PersonMatch[]
  confidence?: number
  isLoading?: boolean
}

const PersonCard: FunctionComponent<{ person: PersonMatch; index: number; onClick?: () => void }> = ({ person, index, onClick }) => (
  <div
    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all hover:-translate-y-0.5 cursor-pointer"
    style={{ animationDelay: `${index * 100}ms` }}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
          <span className="text-purple-700 font-semibold text-sm">
            {(person.firstName?.[0] || "").toUpperCase()}{(person.lastName?.[0] || "").toUpperCase()}
          </span>
        </div>
        <div>
          <h4 className="font-semibold text-gray-900">
            {person.firstName} {person.lastName}
          </h4>
          <p className="text-sm text-gray-500">{person.role || "Team Member"}</p>
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
          {person.overallScore}%
        </span>
        <span className="text-xs text-gray-400">Click for details →</span>
      </div>
    </div>
    <p className="mt-3 text-sm text-gray-600">{person.matchReason}</p>
    {person.strengths && person.strengths.length > 0 && (
      <div className="mt-3 flex flex-wrap gap-1.5">
        {person.strengths.slice(0, 3).map((strength, idx) => (
          <span
            key={idx}
            className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
          >
            {strength}
          </span>
        ))}
      </div>
    )}
  </div>
)

const getScoreColor = (score: number): string => {
  if (score >= 85) return "bg-green-500"
  if (score >= 70) return "bg-green-400"
  if (score >= 55) return "bg-yellow-400"
  if (score >= 40) return "bg-orange-400"
  return "bg-red-400"
}

const getScoreLabel = (score: number): string => {
  if (score >= 85) return "Exceptional"
  if (score >= 70) return "Strong"
  if (score >= 55) return "Good"
  if (score >= 40) return "Developing"
  return "Emerging"
}

const PersonDetailModal: FunctionComponent<{
  person: PersonMatch | null
  open: boolean
  onClose: () => void
}> = ({ person, open, onClose }) => {
  if (!open || !person) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">{person.firstName} {person.lastName}</h2>
            <p className="text-white/80 text-sm">{person.role || "Team Member"}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6">
          {/* Overall Score */}
          <div className="mb-6 text-center">
            <div className="inline-block bg-gradient-to-r from-purple-500 to-purple-600 text-white px-6 py-2 rounded-full text-lg font-semibold">
              Overall Match Score: {person.overallScore}%
            </div>
            <p className="mt-2 text-gray-600 text-sm">{person.matchReason}</p>
          </div>

          {/* Key Strengths */}
          {person.strengths && person.strengths.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-2">Key Strengths</h3>
              <div className="flex flex-wrap gap-2">
                {person.strengths.map((strength, idx) => (
                  <span key={idx} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">
                    {strength}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Measure Scores */}
          {person.measureScores && person.measureScores.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-700 mb-3">Detailed Measure Results</h3>
              <div className="space-y-3">
                {[...person.measureScores]
                  .sort((a, b) => (b.benchmarkScore || 0) - (a.benchmarkScore || 0))
                  .map((measure, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-800">{measure.measureName}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium text-white ${getScoreColor(measure.benchmarkScore)}`}>
                          {getScoreLabel(measure.benchmarkScore)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                              className={`h-2.5 rounded-full ${getScoreColor(measure.benchmarkScore)}`}
                              style={{ width: `${Math.min(measure.benchmarkScore, 100)}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-gray-700 w-16 text-right">
                          {measure.benchmarkScore}/100
                        </span>
                      </div>
                      {measure.percentile && (
                        <p className="text-xs text-gray-500 mt-1">
                          {measure.percentile}th percentile
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {(!person.measureScores || person.measureScores.length === 0) && (
            <p className="text-gray-500 text-center py-4">
              No detailed measure data available for this person.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

const TypingIndicator: FunctionComponent = () => (
  <div className="flex items-center gap-2">
    <div className="flex gap-1">
      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
    <span className="text-gray-500 text-sm">Analyzing cognitive profiles...</span>
  </div>
)

const WhoThinksSection: FunctionComponent<WhoThinksSectionProps> = ({
  showOrgSelector: showOrgSelectorProp,
  standalone = false,
}) => {
  const authStore = useTypedSelector((state: RootState) => state.authSlice)
  const { isRecovering } = useOrganizationRecovery()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const user = authStore.user
  const organization = authStore.organization

  // Check if user is super admin
  const isSuperAdmin =
    user?.roles?.some((r) => r.roleId === RoleName.SiteAdmin) ||
    user?.role === RoleName.SiteAdmin

  // Determine if we should show org selector (prop takes precedence, then auto-detect for super admins)
  const showOrgSelector = showOrgSelectorProp ?? isSuperAdmin

  // Organization selection state for super admins
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)

  // Person detail modal state
  const [selectedPerson, setSelectedPerson] = useState<PersonMatch | null>(null)
  const [showPersonModal, setShowPersonModal] = useState(false)

  const handlePersonClick = (person: PersonMatch) => {
    setSelectedPerson(person)
    setShowPersonModal(true)
  }

  const handleClosePersonModal = () => {
    setShowPersonModal(false)
    setSelectedPerson(null)
  }

  // Fetch organizations for super admin
  const { data: organizationsData, isLoading: isLoadingOrgs } = useGetAdminOrganizationsQuery(undefined, {
    skip: !showOrgSelector,
  })

  // Get effective organization ID
  const defaultOrgId = Number(organization?.id || user?.organizationId || 0)
  const effectiveOrgId = showOrgSelector ? (selectedOrgId || 0) : defaultOrgId

  // Fetch unlock status from API
  const { data: accessStatusData, isLoading: isLoadingAccess } = useGetOrganizationResultsAccessStatusQuery(
    effectiveOrgId,
    { skip: !effectiveOrgId || isRecovering }
  )

  // Unlock based on API response - always unlocked for super admin with org selected
  const isUnlocked = showOrgSelector ? !!selectedOrgId : (accessStatusData?.data?.hasAccess ?? false)

  // Conversation state
  const [messages, setMessages] = useState<ConversationMessage[]>([
    {
      id: "welcome",
      type: "assistant",
      content: "What kind of work needs to get done? Describe a project, challenge, or initiative and I'll find the team members whose thinking style matches the demands.",
    },
  ])
  const [query, setQuery] = useState("")

  // API hooks
  const [processQuery, { isLoading: isProcessing }] = useProcessQueryMutation()
  const [findPeopleMatches] = useFindPeopleMatchesMutation()

  // Handle organization change for super admin
  const handleOrganizationChange = (newOrgId: number) => {
    setSelectedOrgId(newOrgId)
    // Reset conversation when org changes
    setMessages([
      {
        id: "welcome",
        type: "assistant",
        content: "What kind of work needs to get done? Describe a project, challenge, or initiative and I'll find the team members whose thinking style matches the demands.",
      },
    ])
    setQuery("")
  }

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim() || isProcessing) return

    const userMessage: ConversationMessage = {
      id: `user-${Date.now()}`,
      type: "user",
      content: query.trim(),
    }

    const loadingMessage: ConversationMessage = {
      id: `loading-${Date.now()}`,
      type: "assistant",
      content: "",
      isLoading: true,
    }

    setMessages((prev) => [...prev, userMessage, loadingMessage])
    setQuery("")

    try {
      const queryResponse = await processQuery({
        query: userMessage.content,
        organizationId: effectiveOrgId,
      }).unwrap()

      // Remove loading message
      setMessages((prev) => prev.filter((m) => !m.isLoading))

      if (!queryResponse.success) {
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            type: "assistant",
            content: queryResponse.message || "I couldn't process that request. Could you try rephrasing it?",
          },
        ])
        return
      }

      const queryResult = queryResponse.data as QueryResult

      // Get people matches
      let peopleMatches: PersonMatch[] = []
      if (queryResult.primaryRecommendations?.length > 0) {
        const measureIds = queryResult.primaryRecommendations
          .map((r) => r.measure.id)
          .filter((id) => id)

        if (measureIds.length > 0) {
          try {
            const peopleResponse = await findPeopleMatches({
              measureIds,
              organizationId: effectiveOrgId,
              filters: { maxResults: 5, sortBy: "score" },
            }).unwrap()

            if (peopleResponse.success && peopleResponse.data.matches) {
              peopleMatches = peopleResponse.data.matches
            }
          } catch (err) {
            console.warn("Failed to fetch people matches:", err)
          }
        }
      }

      const responseContent = peopleMatches.length > 0
        ? queryResult.explanation
        : "I couldn't find team members with assessment data matching this type of work. Make sure your team has completed their simulations so I can analyze their cognitive profiles."

      setMessages((prev) => [
        ...prev,
        {
          id: `response-${Date.now()}`,
          type: "assistant",
          content: responseContent,
          matches: peopleMatches,
          confidence: queryResult.confidence,
        },
      ])
    } catch (err) {
      console.error("Error:", err)
      setMessages((prev) => prev.filter((m) => !m.isLoading))
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          type: "assistant",
          content: "I'm having trouble connecting right now. Please try again in a moment.",
        },
      ])
    }
  }

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion)
  }

  // Wrapper for standalone mode (includes Sidebar and Header)
  const withLayout = (content: JSX.Element): JSX.Element => {
    if (!standalone) return content
    return (
      <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
        <Sidebar />
        <div className="w-full h-[100dvh] flex-1 flex flex-col overflow-hidden">
          <HeaderSection />
          <div className="flex-1 overflow-auto">
            {content}
          </div>
        </div>
      </div>
    )
  }

  // Show loading state
  if (isLoadingAccess || (showOrgSelector && isLoadingOrgs)) {
    return withLayout(
      <div className="min-h-full bg-gray-50 py-12 px-6 flex items-center justify-center">
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-4 h-4 bg-purple-400 rounded-full"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Show organization selector for super admin when no org selected
  if (showOrgSelector && !selectedOrgId) {
    return withLayout(
      <div className="min-h-full bg-gray-50 py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Who Thinks Like This?</h1>
                <p className="text-sm text-gray-500 mt-1">Select an organization to get started</p>
              </div>
            </div>

            <p className="text-gray-600 leading-relaxed mb-6">
              Match cognitive profiles to project demands. Select an organization to explore their team&apos;s thinking patterns.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organization
              </label>
              {organizationsData?.data && (
                <OrganizationSelector
                  organizations={organizationsData.data}
                  selectedOrganizationId={selectedOrgId}
                  onOrganizationChange={handleOrganizationChange}
                  placeholder="Select an organization..."
                />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show locked state (only for non-super-admins)
  if (!isUnlocked) {
    return withLayout(
      <div className="min-h-full bg-gray-50 py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Who Thinks Like This?</h1>
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded mt-1">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Unlocks after simulation completion
                </span>
              </div>
            </div>

            <p className="text-gray-600 leading-relaxed mb-4">
              Imagine dropping in a project brief and instantly knowing who on your team is built for it. Not who&apos;s available. Not who volunteered last time. Who actually thinks in ways that match what the work demands.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              Task Management. Crisis Response. Strategic Thinking. Information Management. We match the cognitive profile of the assignment to the people most likely to excel—7X more predictive than traditional methods.
            </p>

            <div className="flex flex-col items-start">
              <button
                onClick={() => window.location.href = "/dashboard/recruiter-admin/users/all"}
                className="px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors mb-2"
              >
                Get Your Team to Finish
              </button>
              <span className="text-gray-500 text-xs">30–45 minutes per person. Cognitive matching forever.</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show conversational interface
  return withLayout(
    <>
    <PersonDetailModal
      person={selectedPerson}
      open={showPersonModal}
      onClose={handleClosePersonModal}
    />
    <div className="min-h-full bg-gray-50 py-8 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">Who Thinks Like This?</h1>
                  <p className="text-sm text-gray-500">Match cognitive profiles to project demands</p>
                </div>
              </div>
              {/* Organization selector for super admin */}
              {showOrgSelector && organizationsData?.data && (
                <div className="w-64">
                  <OrganizationSelector
                    organizations={organizationsData.data}
                    selectedOrganizationId={selectedOrgId}
                    onOrganizationChange={handleOrganizationChange}
                    placeholder="Select organization..."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Conversation */}
          <div className="px-6 py-6 min-h-[300px] max-h-[500px] overflow-y-auto">
            <div className="space-y-6">
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${message.type === "user" ? "order-2" : ""}`}>
                    {message.type === "assistant" && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          {message.isLoading ? (
                            <TypingIndicator />
                          ) : (
                            <>
                              <p className="text-gray-700 text-sm leading-relaxed">{message.content}</p>
                              {message.matches && message.matches.length > 0 && (
                                <div className="mt-4 space-y-3">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900">Best matches:</span>
                                    {message.confidence && (
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        message.confidence >= 80
                                          ? "bg-green-100 text-green-700"
                                          : message.confidence >= 60
                                          ? "bg-yellow-100 text-yellow-700"
                                          : "bg-gray-100 text-gray-600"
                                      }`}>
                                        {message.confidence >= 80 ? "High" : message.confidence >= 60 ? "Medium" : "Low"} confidence
                                      </span>
                                    )}
                                  </div>
                                  {message.matches.map((person, idx) => (
                                    <PersonCard key={idx} person={person} index={idx} onClick={() => handlePersonClick(person)} />
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {message.type === "user" && (
                      <div className="bg-purple-600 text-white px-4 py-3 rounded-2xl rounded-br-md">
                        <p className="text-sm">{message.content}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Suggestions - always visible */}
          <div className="px-6 pb-4">
            <p className="text-xs text-gray-500 mb-2">Try these:</p>
            <div className="flex flex-wrap gap-2">
              {[
                "Who should lead our crisis response?",
                "Find someone for strategic planning",
                "Who's best for client communications?",
              ].map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-purple-100 hover:text-purple-700 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex gap-3">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Describe a project or challenge..."
                disabled={isProcessing}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
              />
              <button
                type="submit"
                disabled={isProcessing || !query.trim()}
                className="px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? "Finding..." : "Find"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  )
}

export default WhoThinksSection
